import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getFirestore } from "firebase/firestore";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

// App Check attests that requests come from this app rather than a script
// someone wrote against the public config. The security rules stop one user
// reading another's data; this is what stops anyone with a signup form from
// hammering the database at will.
//
// Inert until VUE_APP_RECAPTCHA_SITE_KEY is set, so it can be rolled out by
// adding the key in Netlify's environment settings. It must run before
// getFirestore/getAuth so the very first request already carries a token.
if (process.env.VUE_APP_RECAPTCHA_SITE_KEY) {
  // Local development has no reCAPTCHA domain; a debug token registered in
  // the Firebase console lets the emulated browser through instead.
  if (process.env.NODE_ENV !== "production" && process.env.VUE_APP_APPCHECK_DEBUG_TOKEN) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.VUE_APP_APPCHECK_DEBUG_TOKEN;
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(process.env.VUE_APP_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

const db = getFirestore(app);
const auth = getAuth(app);

// auth.currentUser is null until Firebase finishes restoring any persisted
// session, which happens asynchronously after page load. Callers that need
// to know the logged-in user before that restoration completes (e.g. the
// router guard on a hard refresh or direct link) must await this instead of
// reading auth.currentUser directly.
const getCurrentUser = () =>
  new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });

export { db, auth, signOut, getCurrentUser };
